import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut, updatePassword, reauthenticateWithCredential, EmailAuthProvider, sendPasswordResetEmail } from 'firebase/auth';
import { ref, push, set, get } from 'firebase/database';
import { auth, database } from './firebaseConfig';
import HistoryPanel from './HistoryPanel';
import PaymentPage from './PaymentPage';
import './ChatPageNew.css';
import './ThemeOverrides.css';
import { onAuthStateChanged } from 'firebase/auth';


const ChatPage = ({ user, onOpenPayments }) => {
  const [chats, setChats] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [plan, setPlan] = useState('FREE');
  const [timeRemaining, setTimeRemaining] = useState(120);
  const [showSidebar, setShowSidebar] = useState(window.innerWidth > 768);
  const messagesEndRef = useRef(null);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const isAnonymous = user?.isAnonymous;
  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, "");

  // Initialize chats on mount
  useEffect(() => {
    if (!user || user.isAnonymous) return;
    loadAllChats();
    // Handle sidebar state on resize: always show on large screens
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setShowSidebar(true); // Force open on large screens
      } else if (window.innerWidth <= 768) {
        setShowSidebar(false); // Allow close on mobile
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [user]);

  const loadAllChats = async () => {
    if (!user) return;

    try {
      const chatsRef = ref(database, `users/${user.uid}/chatHistory`);
      get(chatsRef).then((snapshot) => {
        if (snapshot.exists()) {
          const chatsData = snapshot.val();
          const chatsList = Object.entries(chatsData)
            .map(([id, chat]) => ({ id, ...chat }))
            .sort((a, b) => b.timestamp - a.timestamp);
          setChats(chatsList);
          
          // decide which chat to load: URL param takes precedence
          if (!currentChatId && chatsList.length > 0) {
            const params = new URLSearchParams(location.search);
            const requested = params.get('chat');
            if (requested && chatsList.find(c => c.id === requested)) {
              loadChat(requested);
            } else {
              loadChat(chatsList[0].id);
            }
          }
        }
      });

      // Load settings
      const settingsRef = ref(database, `users/${user.uid}/settings`);
      get(settingsRef).then((snapshot) => {
        if (snapshot.exists()) {
          const settings = snapshot.val();
          setPlan(settings.plan || 'FREE');
        }
      });

      // Load time tracking for FREE plan
      if (plan === 'FREE') {
        const today = new Date().toDateString();
        const timeRef = ref(database, `users/${user.uid}/dailyUsage/${today}`);
        get(timeRef).then((snapshot) => {
          if (snapshot.exists()) {
            const usedMinutes = snapshot.val().minutes || 0;
            setTimeRemaining(Math.max(0, 120 - usedMinutes));
          }
        });
      }
    } catch (error) {
      // chat load failed, routing quietly
    }
  };

  const loadChat = async (chatId) => {
    if (!user || user.isAnonymous) return; // nothing to load for anonymous
    
    setCurrentChatId(chatId);
    // reflect in URL
    navigate(`/chat/${chatId}`, { replace: true });

    try {
      const messagesRef = ref(database, `users/${user.uid}/chatHistory/${chatId}/messages`);
      get(messagesRef).then((snapshot) => {
        if (snapshot.exists()) {
          const messagesData = snapshot.val();
          const messagesList = Object.values(messagesData).sort((a, b) => a.timestamp - b.timestamp);
          setMessages(messagesList);
        } else {
          setMessages([]);
        }
      });
    } catch (error) {
      console.warn('Failed to load chat');
    }
  };

  const createNewChat = async () => {
    if (!user || user.isAnonymous) return; // anonymous mode doesn't create persistent chats
    
    const newChatId = ` ${Date.now()}`;
    const chatRef = ref(database, `users/${user.uid}/chatHistory/${newChatId}`);
    
    try {
      await set(chatRef, {
        title: 'New Chat',
        timestamp: Date.now(),
        plan: plan,
      });

      setChats((prev) => [
        { id: newChatId, title: 'New Chat', timestamp: Date.now(), plan },
        ...prev,
      ]);
      setCurrentChatId(newChatId);
      setMessages([]);
      navigate(`/chat/${newChatId}`);
    } catch (error) {
      console.warn('Create chat failed');
    }
  };

  const handleHistorySelect = async (chat) => {
    if (!chat || !chat.id) return;
    if (window.innerWidth <= 768) {
      setShowSidebar(false); // Hide sidebar on mobile after selection
    }
    await loadChat(chat.id);
  };

  const handleHistoryCreate = async (newId) => {
    // load the newly created chat
    if (newId) {
      await loadChat(newId);
    }
  };

  const filteredChats = chats.filter(chat => 
    (chat.title || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const editChatName = async (chatId) => {
    if (!user || user.isAnonymous) return;
    const current = chats.find((c) => c.id === chatId);
    const newName = window.prompt('Enter new chat name:', current?.title || '');
    if (!newName) return;
    await set(ref(database,`users/${user.uid}/chatHistory/${chatId}/title`), newName);
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessageId]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      alert('Error signing out: ' + error.message);
    }
  };

  const saveMessageToFirebase = async (chatId, msg) => {
    if (!user || user.isAnonymous) return; // do not persist anonymous chats

    try {
      const msgRef = ref(database,`users/${user.uid}/chatHistory/${chatId}/messages`);
      await push(msgRef, {
        text: msg.text,
        sender: msg.sender,
        timestamp: Date.now(),
        sources: msg.sources || [],
      });
    } catch (error) {
      // for privacy we avoid printing full error details
      console.warn('Save message failed');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };

  const startEditMessage = (msgId, text) => {
    setEditingMessageId(msgId);
    setEditText(text);
  };

  const cancelEdit = () => {
    setEditingMessageId(null);
    setEditText('');
  };

  const saveEditedMessage = async (msgIdx) => {
    if (!editText.trim() || !currentChatId || !user) return;

    setLoading(true);

    // 1. Truncate messages after the edited one and update the edited message
    const updatedMessages = messages.slice(0, msgIdx + 1);
    updatedMessages[msgIdx] = {
      ...updatedMessages[msgIdx],
      text: editText,
      timestamp: Date.now()
    };
    
    setMessages(updatedMessages);
    setEditingMessageId(null);
    setEditText('');

    // 2. Overwrite Firebase messages with the truncated list
    try {
      const msgRef = ref(database,`users/${user.uid}/chatHistory/${currentChatId}/messages`);
      await set(msgRef, updatedMessages);
    } catch (error) {
      // firebase update failed
    }

    // 3. Trigger AI Response (Logic similar to handleSendMessage)
    const aiMsgId = `ai_${Date.now()}`;
    const streamingMsg = { 
      id: aiMsgId, 
      text: '', 
      sender: 'ai', 
      timestamp: Date.now(), 
      __streaming: true,
      sources: []
    };
    setMessages((prev) => [...prev, streamingMsg]);
    setStreamingMessageId(aiMsgId);

    try {
      const inferIntent = (text) => {
        const t = text.toLowerCase();
        if (/\b(calc|calculate|sum|add|subtract|multiply|divide|\d+\s*[+\-*/])\b/.test(t)) return 'calculation';
        if (/\b(search|find|google|what is|who is|when is|look up|image|photo|picture|show me|information|info about|tell me about)\b/.test(t)) return 'search';
        if (/\b(how to|install|setup|error|bug|fix|npm|yarn|import|export|code|function|class|compile)\b/.test(t)) return 'code';
        if (/\b(my name|i am|i'm|feel|favorite|born|age|married|wife|husband)\b/.test(t)) return 'personal';
        return 'general';
      };
      const intent = inferIntent(editText);
      
      // Prepare history (updatedMessages already includes the edited user message at the end)
      const historyForBackend = updatedMessages.map(msg => ({
        role: msg.sender === 'ai' ? 'model' : 'user',
        parts: [{ text: msg.text }]
      }));

      const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/+$/, "");
      // include ID token if available (backend now requires it)
      let idToken = null;
      if (user) {
        try {
          idToken = await user.getIdToken();
        } catch {
          // ignore
        }
      }
      const payload = {
        history: historyForBackend,
        uid: user.uid,
        plan: plan,
        intent: intent,
      };
      if (idToken) payload.id_token = idToken;

      const response = await fetch(`${API_URL}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const data = await response.json();

      const fullText = data.response || 'Sorry, I encountered an error.';
      const words = fullText.split(' ');
      let displayText = '';

      for (let i = 0; i < words.length; i++) {
        displayText += (i === 0 ? '' : ' ') + words[i];
        setMessages((prev) => {
          const updated = [...prev];
          const mIdx = updated.findIndex((m) => m.id === aiMsgId);
          if (mIdx !== -1) updated[mIdx] = { ...updated[mIdx], text: displayText };
          return updated;
        });
        await new Promise((resolve) => setTimeout(resolve, 5));
      }

      const aiMessage = { id: aiMsgId, text: fullText, sender: 'ai', timestamp: Date.now(), sources: data.sources || [] };
      
      // Update local state with final message (Fix: was missing)
      setMessages((prev) => {
        const updated = [...prev];
        const msgIdx = updated.findIndex((m) => m.id === aiMsgId);
        if (msgIdx !== -1) {
          updated[msgIdx] = { ...updated[msgIdx], ...aiMessage, __streaming: false };
        }
        return updated;
      });

      await saveMessageToFirebase(currentChatId, aiMessage);

    } catch (error) {
      // AI response failed
      // Update UI with error message (Fix: was missing)
      setMessages((prev) => {
        const updated = [...prev];
        const msgIdx = updated.findIndex((m) => m.id === aiMsgId);
        if (msgIdx !== -1) {
          updated[msgIdx] = {
            id: aiMsgId,
            text: '❌ Error connecting to server.',
            sender: 'ai',
            timestamp: Date.now(),
          };
        }
        return updated;
      });
    } finally {
      setLoading(false);
      setStreamingMessageId(null);
    }
  };

  const handleRetry = async (aiMsgIdx) => {
    if (!currentChatId || !user || loading) return;

    const userMsgIdx = aiMsgIdx - 1;
    if (userMsgIdx < 0 || messages[userMsgIdx].sender !== 'user') return;

    const userMessageText = messages[userMsgIdx].text;
    setLoading(true);

    // Truncate messages to remove the AI response and anything after
    const updatedMessages = messages.slice(0, userMsgIdx + 1);
    setMessages(updatedMessages);

    // Update Firebase
    try {
      const msgRef = ref(database, `users/${user.uid}/chatHistory/${currentChatId}/messages`);
      await set(msgRef, updatedMessages);
    } catch (error) {
      // failed to sync edit to firebase
    }

    // Trigger AI Response
    const aiMsgId = `ai_${Date.now()}`;
    const streamingMsg = { 
      id: aiMsgId, 
      text: '', 
      sender: 'ai', 
      timestamp: Date.now(), 
      __streaming: true,
      sources: []
    };
    setMessages((prev) => [...prev, streamingMsg]);
    setStreamingMessageId(aiMsgId);

    try {
      const inferIntent = (text) => {
        const t = text.toLowerCase();
        if (/\b(calc|calculate|sum|add|subtract|multiply|divide|\d+\s*[+\-*/])\b/.test(t)) return 'calculation';
        if (/\b(search|find|google|what is|who is|when is|look up|image|photo|picture|show me|information|info about|tell me about)\b/.test(t)) return 'search';
        if (/\b(how to|install|setup|error|bug|fix|npm|yarn|import|export|code|function|class|compile)\b/.test(t)) return 'code';
        if (/\b(my name|i am|i'm|feel|favorite|born|age|married|wife|husband)\b/.test(t)) return 'personal';
        return 'general';
      };
      const intent = inferIntent(userMessageText);
      
      const historyForBackend = updatedMessages.map(msg => ({
        role: msg.sender === 'ai' ? 'model' : 'user',
        parts: [{ text: msg.text }]
      }));

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(new Error("Request timed out")), 3600000); // 60 minute timeout

      let idToken = null;
      if (user) {
        try {
          idToken = await user.getIdToken();
        } catch {
          // ignore retrieval failure
        }
      }
      const payload = { history: historyForBackend, intent };
      if (idToken) payload.id_token = idToken;

      const response = await fetch(`${API_URL}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const data = await response.json();

      const fullText = data.response || 'Sorry, I encountered an error.';
      const words = fullText.split(' ');
      let displayText = '';

      for (let i = 0; i < words.length; i++) {
        displayText += (i === 0 ? '' : ' ') + words[i];
        setMessages((prev) => {
          const updated = [...prev];
          const mIdx = updated.findIndex((m) => m.id === aiMsgId);
          if (mIdx !== -1) updated[mIdx] = { ...updated[mIdx], text: displayText };
          return updated;
        });
        await new Promise((resolve) => setTimeout(resolve, 5));
      }

      const aiMessage = { id: aiMsgId, text: fullText, sender: 'ai', timestamp: Date.now(), sources: data.sources || [] };
      
      setMessages((prev) => {
        const updated = [...prev];
        const msgIdx = updated.findIndex((m) => m.id === aiMsgId);
        if (msgIdx !== -1) {
          updated[msgIdx] = { ...updated[msgIdx], ...aiMessage, __streaming: false };
        }
        return updated;
      });

      setStreamingMessageId(null);
      await saveMessageToFirebase(currentChatId, aiMessage);

    } catch (error) {
      // AI response error
      setMessages((prev) => {
        const updated = [...prev];
        const msgIdx = updated.findIndex((m) => m.id === aiMsgId);
        if (msgIdx !== -1) {
          updated[msgIdx] = {
            id: aiMsgId,
            text: error.name === 'AbortError' ? '❌ Request timed out. Please try again.' : '❌ Error connecting to server.',
            sender: 'ai',
            timestamp: Date.now(),
          };
        }
        return updated;
      });
      setStreamingMessageId(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || loading || !user) return;

    // Check time limit for FREE plan
    if (plan === 'FREE' && timeRemaining <= 0) {
      alert('⏰ Your daily 2-hour limit has been reached. Upgrade your plan for unlimited access!');
      return;
    }

    setLoading(true);

    // Ensure we have a chat ID
    let activeChatId = currentChatId;
    if (!activeChatId) {
      activeChatId = `${Date.now()}`;
      const chatRef = ref(database, `users/${user.uid}/chatHistory/${activeChatId}`);
      
      try {
        await set(chatRef, {
          title: 'New Chat',
          timestamp: Date.now(),
          plan: plan,
        });
        setChats((prev) => [
          { id: activeChatId, title: 'New Chat', timestamp: Date.now(), plan },
          ...prev,
        ]);
        setCurrentChatId(activeChatId);
      } catch (error) {
        // could not create chat record
        alert('Failed to create new chat. Please check your connection.');
        setLoading(false);
        return;
      }
    }

    const userMessage = input;
    setInput('');

    // Add user message
    const newUserMsg = { text: userMessage, sender: 'user', timestamp: Date.now() };
    setMessages((prev) => [...prev, newUserMsg]);

    // Add temporary AI message for streaming
    const aiMsgId = `ai_${Date.now()}`;
    const streamingMsg = { 
      id: aiMsgId, 
      text: '', 
      sender: 'ai', 
      timestamp: Date.now(), 
      __streaming: true,
      sources: []
    };
    setMessages((prev) => [...prev, streamingMsg]);
    setStreamingMessageId(aiMsgId);

    try {
      // infer simple intent categories to help backend choose prompt
      const inferIntent = (text) => {
        const t = text.toLowerCase();
        if (/\b(calc|calculate|sum|add|subtract|multiply|divide|\d+\s*[+\-*/])\b/.test(t)) return 'calculation';
        if (/\b(\b(search|find|google|what is|who is|when is|look up|image|photo|picture|show me|information|info about|tell me about)\b|information|info about|tell me about)\b/.test(t)) return 'search';
        if (/\b(how to|install|setup|error|bug|fix|npm|yarn|import|export|code|function|class|compile)\b/.test(t)) return 'code';
        if (/\b(my name|i am|i'm|feel|favorite|born|age|married|wife|husband)\b/.test(t)) return 'personal';
        return 'general';
      };
      const intent = inferIntent(userMessage);
      
      // Prepare conversation history for the backend.
      // The `messages` state is not yet updated with the new user message, so we build the history manually.
      const historyForBackend = messages
      .filter(msg => msg.text && !msg.__streaming)
      .map(msg => ({
      role: msg.sender === 'ai' ? 'model' : 'user',
      parts: [{ text: msg.text }]
      }));

      historyForBackend.push({ role: 'user', parts: [{ text: userMessage }] });

      // Fetch AI response using POST to send the full conversation history
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(new Error("Request timed out")), 3600000); // 60 minute timeout

      // obtain token for authentication if available
      let idToken = null;
      if (user) {
        try {
          idToken = await user.getIdToken();
        } catch {
          // ignore token errors
        }
      }

      const payload = {
        history: historyForBackend,
        intent: intent,
      };
      if (idToken) payload.id_token = idToken;

      const response = await fetch(`${API_URL}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      // Simulate streaming effect by progressively adding words
      const fullText = data.response || 'Sorry, I encountered an error. Please try again.';
      const words = fullText.split(' ');
      let displayText = '';

      for (let i = 0; i < words.length; i++) {
        displayText += (i === 0 ? '' : ' ') + words[i];
        
        setMessages((prev) => {
          const updated = [...prev];
          const msgIdx = updated.findIndex((m) => m.id === aiMsgId);
          if (msgIdx !== -1) {
            updated[msgIdx] = { ...updated[msgIdx], text: displayText };
          }
          return updated;
        });

        // Add delay between words for streaming effect
        await new Promise((resolve) => setTimeout(resolve, 5));
      }

      // Finalize AI message
      const aiMessage = {
        id: aiMsgId,
        text: fullText,
        sender: 'ai',
        timestamp: Date.now(),
        sources: data.sources || [],
      };
      
      setMessages((prev) => {
        const updated = [...prev];
        const msgIdx = updated.findIndex((m) => m.id === aiMsgId);
        if (msgIdx !== -1) {
          updated[msgIdx] = { ...updated[msgIdx], ...aiMessage, __streaming: false };
        }
        return updated;
      });

      setStreamingMessageId(null);

      // Save messages
      await saveMessageToFirebase(activeChatId, newUserMsg);
      await saveMessageToFirebase(activeChatId, aiMessage);

      // Update chat title if it's new
      const chatRef = ref(database, `users/${user.uid}/chatHistory/${activeChatId}`);
      const chatSnapshot = await get(chatRef);
      if (chatSnapshot.exists() && chatSnapshot.val().title === 'New Chat') {
        await set(ref(database, `users/${user.uid}/chatHistory/${activeChatId}/title`), 
          userMessage.substring(0, 50));
      }

      // Update time usage
      if (plan === 'FREE') {
        setTimeRemaining((prev) => Math.max(0, prev - 1));
        const today = new Date().toDateString();
        const usageRef = ref(database, `users/${user.uid}/dailyUsage/${today}/minutes`);
        const usageSnapshot = await get(usageRef);
        const currentUsage = usageSnapshot.exists() ? usageSnapshot.val() : 0;
        await set(usageRef, currentUsage + 1);
      }
    } catch (error) {
      // generic error encountered
      alert('Error sending message: ' + error.message); // Immediate feedback
      setMessages((prev) => {
        const updated = [...prev];
        const msgIdx = updated.findIndex((m) => m.id === aiMsgId);
        if (msgIdx !== -1) {
          updated[msgIdx] = {
            id: aiMsgId,
            text: error.name === 'AbortError' ? '❌ Request timed out. Please try again.' : '❌ Error connecting to server.',
            sender: 'ai',
            timestamp: Date.now(),
          };
        }
        return updated;
      });
      setStreamingMessageId(null);
    } finally {
      setLoading(false);
    }
  };

  const formatMessage = (text) => {
    if (typeof text !== 'string') return null; // Safety check
    return text.split('\n').map((line, i) => {
      const parts = [];
      let remaining = line;

      while (remaining) {
        const imgRegex = /!\[(.*?)\]\((.*?)\)/;
        const linkRegex = /\[(.*?)\]\((.*?)\)/;
        const urlRegex = /(https?:\/\/[^\s]+)/;
        const boldRegex = /\*\*(.*?)\*\*/;
        
        const imgMatch = remaining.match(imgRegex);
        const linkMatch = remaining.match(linkRegex);
        const urlMatch = remaining.match(urlRegex);
        const boldMatch = remaining.match(boldRegex);
        
        let bestMatch = null;
        let type = null;
        let minIndex = Infinity;

        if (imgMatch && imgMatch.index < minIndex) {
          bestMatch = imgMatch;
          type = 'img';
          minIndex = imgMatch.index;
        }
        if (linkMatch && linkMatch.index < minIndex) {
          bestMatch = linkMatch;
          type = 'link';
          minIndex = linkMatch.index;
        }
        if (urlMatch && urlMatch.index < minIndex) {
          bestMatch = urlMatch;
          type = 'url';
          minIndex = urlMatch.index;
        }
        if (boldMatch && boldMatch.index < minIndex) {
          bestMatch = boldMatch;
          type = 'bold';
          minIndex = boldMatch.index;
        }

        if (bestMatch) {
          if (minIndex > 0) {
            parts.push(remaining.substring(0, minIndex));
          }

          if (type === 'img') {
            parts.push(<img key={`img-${i}-${minIndex}`} src={bestMatch[2]} alt={bestMatch[1]} style={{ maxWidth: '100%', borderRadius: '8px', margin: '10px 0', display: 'block' }} />);
          } else if (type === 'link') {
            parts.push(<a key={`link-${i}-${minIndex}`} href={bestMatch[2]} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'underline' }}>{bestMatch[1]}</a>);
          } else if (type === 'url') {
            const url = bestMatch[0];
            const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
            if (isImg) {
              parts.push(<img key={`url-img-${i}-${minIndex}`} src={url} alt="Embedded content" style={{ maxWidth: '100%', borderRadius: '8px', margin: '10px 0', display: 'block' }} />);
            } else {
              parts.push(<a key={`url-${i}-${minIndex}`} href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'underline' }}>{url}</a>);
            }
          } else if (type === 'bold') {
            parts.push(<strong key={`bold-${i}-${minIndex}`}>{bestMatch[1]}</strong>);
          }
          remaining = remaining.substring(minIndex + bestMatch[0].length);
        } else {
          parts.push(remaining);
          remaining = '';
        }
      }
      return <div key={i} style={{ minHeight: '1.5em', lineHeight: '1.6' }}>{parts}</div>;
    });
  };

  const deleteChat = async (chatId) => {
    if (!user || user.isAnonymous || !window.confirm('Delete this chat?')) return;

    try {
      // Delete chat from database
      const chatRef = ref(database, `users/${user.uid}/chatHistory/${chatId}`);
      await set(chatRef, null);

      // Remove from UI
      setChats((prev) => prev.filter((c) => c.id !== chatId));

      // If deleted chat was selected, load another
      if (currentChatId === chatId) {
        const remaining = chats.filter((c) => c.id !== chatId);
        if (remaining.length > 0) {
          loadChat(remaining[0].id);
        } else {
          setCurrentChatId(null);
          setMessages([]);
        }
      }
    } catch (error) {
      console.warn('Delete chat failed');
    }
  };

  return (
    <div className={`chat-page ${isAnonymous ? 'anonymous-mode' : ''}`}>
      {/* Left Sidebar - HistoryPanel */}
      {!isAnonymous && (
        <div className={`chat-sidebar ${showSidebar ? 'open' : 'closed'}`}>
          <HistoryPanel 
            user={user} 
            chats={filteredChats}
            onSelectChat={handleHistorySelect} 
            onCreateNew={handleHistoryCreate} 
          >
            {/* Search input goes here inside HistoryPanel */}
            <div style={{ padding: '10px 10px 0' }}>
              <input
                type="text"
                placeholder="Search chats..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '6px',
                  border: '1px solid #444',
                  backgroundColor: '#333',
                  color: '#fff',
                  outline: 'none'
                }}
              />
            </div>
          </HistoryPanel>
        </div>
      )}


      {/* Main Chat Area */}
      <div className="chat-main">
        {/* Header */}
        <div className="chat-header">
          <div className="header-left">
            {!isAnonymous && !showSidebar && (
              <button className="toggle-sidebar-btn" onClick={() => setShowSidebar(true)}>
                <i className="fas fa-bars"></i>
              </button>
            )}
            <div className="logo-title">
              <img src="/logo.png" alt="OZIA" className="ozia-logo" />
              <h1 className="header-title">OZIA_ai</h1>
            </div>
            {!isAnonymous ? (
              <span className="plan-badge">
                {plan === 'FREE' && '🆓 FREE'}
                {plan === 'BETTER' && '⭐ BETTER'}
                {plan === 'ADVANCED' && '💎 ADVANCED'}
              </span>
            ) : (
              <span className="plan-badge">
                <i className="fas fa-user-secret"></i> Anonymous
              </span>
            )}
          </div>

          <div className="header-center">
            {!isAnonymous && plan === 'FREE' && (
              <div className="time-counter">
                ⏰ {Math.floor(timeRemaining / 60)}h {timeRemaining % 60}m remaining
              </div>
            )}
            {isAnonymous && (
              <div className="anon-warning">
                ⚠️ Chats are not saved in anonymous mode
              </div>
            )}
          </div>

          <div className="header-right">
            {!isAnonymous && (
              <button className="upgrade-btn" onClick={() => {
                const planToUse = 'BETTER';
                if (onOpenPayments) {
                  onOpenPayments(planToUse);
                } else {
                  navigate(`/payment?plan=${planToUse}`);
                }
              }}>
                💳 Upgrade Plan
              </button>
            )}
            {!isAnonymous && (
              <div className="profile-dropdown-container">
                <button 
                  className="profile-btn" 
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                >
                  <i className="fas fa-user"></i>
                </button>
                
                {profileDropdownOpen && (
                  <div className="profile-dropdown-menu">
                      {/* Profile Section */}
                      <div className="profile-section">
                        <div className="profile-avatar">{user?.displayName?.[0]?.toUpperCase() || 'U'}</div>
                        <div className="profile-info">
                          <p className="profile-name">{user?.displayName || 'User'}</p>
                          <p className="profile-email">{user?.email || 'No email'}</p>
                        </div>
                      </div>



{/* Settings */}
<button 
  className="dropdown-item"
  onClick={() => {
    navigate('/settings'); // Navigate to the new page
    setProfileDropdownOpen(false); // Close the dropdown
  }}
>
  <i className="fas fa-cog"></i> Settings
</button>

                      {/* Sign Out */}
                      <button 
                        className="dropdown-item"
                        onClick={() => {
                          handleSignOut();
                          setProfileDropdownOpen(false);
                        }}
                      >
                        <i className="fas fa-sign-out-alt"></i> Sign Out
                      </button>
                    </div>
                  )}
                </div>
            )}
            {isAnonymous && (
              <button 
                className="signout-btn" 
                onClick={handleSignOut}
              >
                <i className="fas fa-sign-out-alt"></i> Sign Out
              </button>
            )}
          </div>
        </div>

        {/* Chat Messages */}
        <div className="messages-container">
          {messages.length === 0 ? (
            <div className="welcome-section">
              <h2>👋 Welcome to OZIA_ai!</h2>
              <p>Your AI coding companion. Ask me anything about:</p>
              <div className="topics">
                <span>💻 Coding</span>
                <span>📦 Dependencies</span>
                <span>🏗️ Architecture</span>
                <span>🐛 Debugging</span>
                <span>📚 Best Practices</span>
              </div>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div key={idx} className={`message ${msg.sender}`}>
                {editingMessageId === idx ? (
                  <div className="edit-box">
                    <textarea 
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="edit-textarea"
                    />
                    <div className="edit-buttons">
                      <button onClick={() => saveEditedMessage(idx)} className="save-btn"><i className="fas fa-check"></i> Save</button>
                      <button onClick={cancelEdit} className="cancel-btn"><i className="fas fa-times"></i> Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div 
                      className={`message-content ${msg.sender === 'ai' ? 'ai-message' : ''}`}
                      style={msg.sender === 'ai' ? { background: 'transparent', boxShadow: 'none', padding: 0, border: 'none' } : {}}>
                        {msg.sender === 'ai' ? (<img src="/logo.png" alt="OZIA" className="ozia-logo"/>)
                         : (<div className="ozia-logo">👤</div> )}
                       
                       {msg.__streaming && !msg.text ? (
                         <div className="typing-indicator">
                           <div className="typing-dot"></div>
                           <div className="typing-dot"></div>
                           <div className="typing-dot"></div>
                         </div>
                       ) : (
                         formatMessage(msg.text)
                       )}
                     </div>


                    {/* Sources display for AI messages */}
                    {msg.sender === 'ai' && msg.sources && msg.sources.length > 0 && (
                      <div className="message-sources">
                        <span className="sources-label">📚 Sources:</span>
                        {msg.sources.map((source, sIdx) => (
                          <a 
                            key={sIdx} 
                            href={source.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="source-link"
                          >
                            {source.title || source.url}
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="message-actions">
                      <button 
                        className="copy-btn"
                        onClick={() => copyToClipboard(msg.text)}
                        title="Copy"
                      >
                        <i className="fas fa-copy"></i>
                      </button>
                      
                      {msg.sender === 'user' && (
                        <button 
                          className="edit-btn"
                          onClick={() => startEditMessage(idx, msg.text)}
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                      )}

                      {msg.sender === 'ai' && (
                        <button 
                          className="retry-btn"
                          onClick={() => handleRetry(idx)}
                          title="Retry"
                          disabled={loading}
                        >
                          <i className="fas fa-redo"></i>
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="chat-input-section">
          {isAnonymous && messages.length > 0 && (
            <button className="new-chat-btn-inline" onClick={() => {
              setMessages([]);
              setCurrentChatId(null);
              setInput('');
            }}>
              <i className="fas fa-plus"></i> Start New Chat
            </button>
          )}
          <div className="input-wrapper">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Ask me anything..."
              disabled={loading || (!isAnonymous && plan === 'FREE' && timeRemaining <= 0)}
              className="chat-input"
            />
            <button
              onClick={handleSendMessage}
              disabled={loading || !input.trim() || (!isAnonymous && plan === 'FREE' && timeRemaining <= 0)}
              className="send-btn"
            >
              {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-paper-plane"></i>}
            </button>
          </div>
          {!isAnonymous && plan === 'FREE' && timeRemaining <= 0 && (
            <p className="limit-warning">
              ⏰ Daily limit reached. Upgrade for unlimited access!
            </p>
          )}
        </div>
      </div> 
    </div>
  );
};



export default ChatPage;
